import { streamChat } from "../lib/streaming";
import { parseEvalTail } from "./prompt";
import type { ChatMessage } from "../types";

/**
 * Median-of-N scoring (PROTOTYPE).
 *
 * The coach's per-field score is non-deterministic run-to-run even at
 * temperature 0: on Nic's TSLIT canvas ~19% of fields flip ±1 between two
 * separate scoring sessions, swinging the composite ±0.1 of a grade. The wobble
 * is concentrated on borderline 4/5 operational answers. Scoring each field N
 * times and taking the median collapses that within-session wobble to a single
 * stable value (measured: median-of-3 removes the borderline flips a single
 * sample shows). It does NOT change the grade level and does not fully remove
 * cross-session drift — see docs; this is a reproducibility fix, not alignment.
 *
 * Cost: N× scoring tokens per field. Set SCORING_SAMPLES = 1 to disable.
 */
export const SCORING_SAMPLES = 3;

export function median(nums: number[]): number {
  const s = [...nums].sort((a, b) => a - b);
  return s[Math.floor(s.length / 2)];
}

export interface ScoreCallParams {
  url: string;
  sessionId: string;
  system: string;
  messages: ChatMessage[];
  model?: string;
  signal: AbortSignal;
}

/** One score-only scoring call. Returns the parsed 1–5 score, or null on failure. */
function scoreOnce(p: ScoreCallParams): Promise<number | null> {
  let acc = "";
  return new Promise((resolve) => {
    streamChat({
      url: p.url,
      sessionId: p.sessionId,
      system: p.system,
      messages: p.messages,
      provider: "anthropic",
      model: p.model,
      signal: p.signal,
      onText: (d) => {
        acc += d;
      },
      onDone: () => {
        const parsed = parseEvalTail(acc);
        resolve(parsed ? parsed.score : null);
      },
      onError: () => resolve(null),
    });
  });
}

/**
 * Given the score from the visible (prose) scoring call, draw SCORING_SAMPLES-1
 * more independent scores for the same field and return the median of all of
 * them. Extra samples run in parallel; failures are dropped. Falls back to the
 * primary score if every extra sample fails.
 */
export async function medianScore(
  primary: number,
  p: ScoreCallParams,
): Promise<{ score: number; samples: number[] }> {
  const extra = SCORING_SAMPLES - 1;
  if (extra <= 0) return { score: primary, samples: [primary] };
  const results = await Promise.all(Array.from({ length: extra }, () => scoreOnce(p)));
  const samples = [primary, ...results.filter((n): n is number => n != null)];
  return { score: median(samples), samples };
}
